import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { t } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { SAVED_CONFIRMATION_MS, type SaveOutcome } from '../../components/controls/SavedConfirmation';
import styles from './saveNote.module.css';

/**
 * The note a Settings card's Save leaves beside itself (decision 2760; T1618).
 * *Saved.* and a refusal stand in the card's own actions row, next to the Save
 * that was pressed, never at the top of a page that may run three screens down.
 *
 * 🔒 It is the Alert Banner in the one shape every message has (decision 1326),
 * so only its place differs. A success clears itself after the same four seconds
 * the Saved Confirmation stands (decision 1325); a refusal stays until it is
 * dismissed. 🔨 A card holds its own note, so a page of eighteen cards says
 * which of them saved.
 */
export interface SaveNoteProps {
  /** The words, or null for none. The saved sentence reads as a success unless `tone` says otherwise. */
  message: string | null;
  tone?: SaveOutcome['tone'];
  onClear: () => void;
  /** What the four seconds restarts on, where two identical answers in a row are two saves. */
  renewedBy?: unknown;
}

export function SaveNote({ message, tone, onClear, renewedBy }: SaveNoteProps) {
  const shown = tone ?? (message === t('settings.saved') ? 'good' : 'error');
  useEffect(() => {
    if (message === null || shown !== 'good') return;
    const timer = window.setTimeout(onClear, SAVED_CONFIRMATION_MS);
    return () => window.clearTimeout(timer);
    // The note restarts its four seconds when the words or the tone change, not when the page redraws.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, shown, renewedBy]);
  if (message === null) return null;
  return (
    <div className={styles.note} data-save-note={shown}>
      <AlertBanner tone={shown} message={message} action={{ label: t('modal.dismiss'), onAction: onClear }} />
    </div>
  );
}

/** A save's outcome object as the note reads it, for a page that holds `{ tone, text }`. */
export function SaveNoteFor({ outcome, onClear }: { outcome: SaveOutcome | null; onClear: () => void }) {
  return <SaveNote message={outcome?.text ?? null} {...(outcome ? { tone: outcome.tone } : {})} onClear={onClear} renewedBy={outcome} />;
}

/**
 * The note and the setter a card holds for its own Save: the pair `useNotice`
 * gives a page, drawn beside the Save instead. A page of several Saves calls it
 * once per card, so each card answers for itself.
 */
export function useSaveNote(): [ReactNode, (outcome: SaveOutcome | null) => void] {
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const clear = useCallback(() => setOutcome(null), []);
  const node = <SaveNoteFor outcome={outcome} onClear={clear} />;
  return [node, setOutcome];
}

/**
 * The notes of a page that holds several cards behind one write: each card
 * reports under its own key and draws `at(key)` beside its own Save, so the
 * answer to a Save lands in the card that was pressed and in no other.
 */
export function useSaveNotes(): {
  at: (card: string) => ReactNode;
  report: (card: string, outcome: SaveOutcome | null) => void;
  /** The same for a page that holds its answer as one sentence: the saved sentence is a success and any other is a refusal, unless the tone is given. */
  say: (card: string, message: string | null, tone?: SaveOutcome['tone']) => void;
} {
  const [outcomes, setOutcomes] = useState<Readonly<Record<string, SaveOutcome | null>>>({});
  const report = useCallback((card: string, outcome: SaveOutcome | null) => setOutcomes((was) => ({ ...was, [card]: outcome })), []);
  const say = useCallback((card: string, message: string | null, tone?: SaveOutcome['tone']) => report(card, message === null ? null : { tone: tone ?? (message === t('settings.saved') ? 'good' : 'error'), text: message }), [report]);
  const at = (card: string): ReactNode => <SaveNoteFor key={card} outcome={outcomes[card] ?? null} onClear={() => report(card, null)} />;
  return { at, report, say };
}
